/**
 * useAuth — Zustand store for SSO authentication via app.ars93.com
 *
 * Calls GET /auth/me (proxied to auth-gateway) which reads the httpOnly
 * cookie ars93_access, validates the JWT, and returns the user profile.
 */

import { create } from 'zustand';

const PORTAL_LOGIN = 'https://app.ars93.com/login';

export interface AuthUser {
  user_id: string;
  email: string;
  org_id: string;
  org_name: string;
  role: string;
  features: string[];
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'forbidden' | 'error';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;

  fetchMe: () => Promise<void>;
  logout: () => void;
  hasFeature: (slug: string) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  error: null,

  fetchMe: async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });

      if (res.status === 401) {
        set({ status: 'unauthenticated', user: null, error: null });
        return;
      }

      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        set({ status: 'forbidden', user: null, error: data.error || 'no_app_access' });
        return;
      }

      if (!res.ok) {
        set({ status: 'error', user: null, error: `HTTP ${res.status}` });
        return;
      }

      const user: AuthUser = await res.json();
      set({ status: 'authenticated', user, error: null });
    } catch {
      set({ status: 'error', user: null, error: 'network' });
    }
  },

  logout: () => {
    // SSO logout: redirect to portal which clears the .ars93.com cookie
    window.location.href = `${PORTAL_LOGIN}?redirect=${encodeURIComponent(window.location.href)}`;
  },

  hasFeature: (slug: string) => {
    const { user } = get();
    return user?.features.includes(slug) ?? false;
  },
}));

/** Redirect to portal login with return URL */
export function redirectToLogin() {
  window.location.href = `${PORTAL_LOGIN}?redirect=${encodeURIComponent(window.location.href)}`;
}
